import { useCallback } from 'react';
import {
  InfiniteData,
  keepPreviousData,
  QueryFunctionContext,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query-v5';
import { useUserSetupStore } from '@sib/auth';
import api from '../api';
import {
  CurrentUserDto,
  InviteUserBodyDto,
  EntraGroup,
  UserOrganizationsListDto,
} from '../api-client';
import { QueryKey } from '../enums';
import { PaginationOptions } from '../types';
import { buildFilter } from '../utils';

const useSetupUser = () => {
  const {
    organizationId,
    setOrganizations,
    setUserRoles,
    setOrganizationId,
    setOrganizationName,
  } = useUserSetupStore();

  const setupUser = useCallback(
    (data: CurrentUserDto | null | undefined) => {
      if (!data) return;

      if (organizationId) return;

      const rolesToSet = data?.roles as unknown as string[];

      const isSibEmployee =
        rolesToSet.includes(EntraGroup.ClientResultManager) ||
        rolesToSet.includes(EntraGroup.Employee);

      setUserRoles(rolesToSet);

      if (data?.organizations?.length && !isSibEmployee) {
        try {
          setOrganizations(data.organizations);

          const newOrganization = data.organizations[0];

          const shouldAssignNewOrganization =
            !data.organizations.some(
              organization => organization.id === organizationId
            ) && !isSibEmployee;
          if (shouldAssignNewOrganization) {
            setOrganizationId(newOrganization.id);
            setOrganizationName(newOrganization.name);
          }
        } catch {
          setOrganizationId('');

          return setOrganizationName('');
        }
      }

      if (!organizationId && data?.organizations?.length && isSibEmployee) {
        const newOrganization = data.organizations[0];
        setOrganizationId(newOrganization.id);
        setOrganizationName(newOrganization.name);
      }
    },
    [
      organizationId,
      setOrganizations,
      setUserRoles,
      setOrganizationId,
      setOrganizationName,
    ]
  );

  return setupUser;
};

export const useGetUser = () => {
  const setupUser = useSetupUser();

  const queryResult = useQuery({
    queryKey: [QueryKey.Resource.User],
    queryFn: async () => {
      const data = await api.user.usersControllerGetCurrentUser();

      setupUser(data);

      return data;
    },
  });

  return queryResult;
};

export const useGetUserOrganizations = (
  paginationOptions: PaginationOptions,
  options?: UseQueryOptions<UserOrganizationsListDto>
): UseQueryResult<UserOrganizationsListDto> =>
  useQuery({
    placeholderData: keepPreviousData,
    ...options,
    queryKey: [
      QueryKey.Resource.UserOrganizations,
      paginationOptions.page,
      paginationOptions.filter,
    ],

    queryFn: () =>
      api.user.usersControllerGetUserOrganizations(paginationOptions),
  });

const organizationsQueryFn = async (
  { pageParam = 1 }: QueryFunctionContext<readonly unknown[], unknown>,
  { search, size, sort }: Omit<PaginationOptions, 'page'> & { search?: string }
) => {
  //TODO: Implement mechanism for current api to handle filters
  const filter = search
    ? buildFilter({
        field: 'name',
        operator: 'contains',
        value: search,
      })
    : null;
  const response = await api.user.usersControllerGetUserOrganizations({
    page: Number(pageParam),
    size,
    ...(sort && {
      sort,
    }),
    ...(filter && {
      filter: [filter],
    }),
  });

  return response;
};

export const useInfiniteOrganizations = (
  { page, search = '', size, sort }: PaginationOptions & { search?: string },
  options?: Partial<
    UseInfiniteQueryOptions<
      UserOrganizationsListDto,
      Error,
      InfiniteData<UserOrganizationsListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [QueryKey.Resource.UserOrganizations, search],
    queryFn: pageParams =>
      organizationsQueryFn(pageParams, { size, search, ...(sort && { sort }) }),
    getNextPageParam: lastPage => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: page || 1,
  });

interface InvitedUserDto {
  email: string;
  name: string;
  oid: string;
  imageUrl?: string;
}

export const useGetInvitedUsers = () => {
  return useQuery({
    queryKey: [QueryKey.Resource.InvitedUsers],
    queryFn: async () =>
      (await api.user.usersControllerGetInvitedUsers({
        withAccessToken: true,
      })) as InvitedUserDto[],
  });
};

//TODO: Remove types once api-client is updated
interface InvitedUser {
  createdDateTime: string;
  email: string;
  name: string;
  oid: string;
}

interface InviteUserError {
  email: string;
  error: string;
}

interface InviteUserResponse {
  success: InvitedUser[];
  failed: InviteUserError[];
}

export const useInviteUser = (
  options?: UseMutationOptions<
    InviteUserResponse,
    Error,
    InviteUserBodyDto & { organizationId: string }
  >
) =>
  useMutation({
    ...options,
    mutationFn: async ({ organizationId, ...data }) =>
      api.user.usersControllerInviteUser(organizationId, data, {
        withAccessToken: true,
      }) as unknown as Promise<InviteUserResponse>,
  });

interface DeleteInvitedUserPayload {
  userId: string;
}

export const useDeleteInvitedUser = (
  options?: UseMutationOptions<void, Error, DeleteInvitedUserPayload>
) =>
  useMutation({
    ...options,
    mutationFn: async ({ userId }) =>
      api.user.usersControllerDeleteInvitedUser(userId, {
        withAccessToken: true,
      }),
  });
